import { Button, Divider, Group, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import type { AdPlatform } from '@marketing-tool/shared';
import { Line, Meta } from '@marketing-tool/shared';

import type { Ad } from '../../../data/game-report/types';
import { preventEnterSubmit } from '../../../lib/utils';

export type EditAdFormData = {
    id: string | number;
    name?: string;
    configuredStatus?: string;
};

type EditAdFormProps = {
    platform: AdPlatform;
    ad: Ad;
    onSubmit: (data: EditAdFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const EditAdForm = ({ platform, ad, onSubmit, onCancel, isSubmitting }: EditAdFormProps) => {
    const form = useForm<EditAdFormData>({
        initialValues: {
            id: ad.id,
            name: ad.name,
            configuredStatus: ad.configuredStatus,
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Only send changed fields
        const updates: EditAdFormData = { id: ad.id };
        if (form.isDirty('name')) updates.name = values.name;
        if (form.isDirty('configuredStatus')) updates.configuredStatus = values.configuredStatus;

        onSubmit(updates);
    });

    const statusOptions =
        platform === 'line'
            ? [
                  { value: Line.ConfiguredStatus.Active, label: 'Active' },
                  { value: Line.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Line.ConfiguredStatus.Draft, label: 'Draft' },
              ]
            : [
                  { value: Meta.ConfiguredStatus.Active, label: 'Active' },
                  { value: Meta.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Meta.ConfiguredStatus.Archived, label: 'Archived' },
              ];

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <TextInput
                    label="Ad Name"
                    placeholder="Enter ad name"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('name')}
                />

                <Select
                    label="Status"
                    required
                    data={statusOptions}
                    disabled={isSubmitting}
                    value={form.values.configuredStatus}
                    onChange={(val) => {
                        if (val !== null) form.setFieldValue('configuredStatus', val);
                    }}
                />

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
